import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs, { type Dayjs } from 'dayjs';
import useSWR from 'swr';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Select from '@/components/@IntranetLibrary/Select';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildProductionCostClosingContentHtml,
  ProductionCostClosingSnapshot,
  type ProductionCostClosingDetailSource,
} from '@/components/Approval/@components/ProductionCostClosing';
import { getProductionCostClosingDraftSourceUrl } from '@/constant/approval';
import useMyBrandOptions from '@/hooks/approval/useMyBrandOptions';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildProductionCostClosingSubject,
  productionCostClosingCaseTitle,
  type ProductionCostClosingDraftSource,
} from '@/utils/approval/productionCostClosing';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = {
  isEditMode: boolean;
  /** 저장된 문서번호 — 편집 복원 본문의 상세보기 조회용 */
  documentId?: string;
};

/**
 * GERP 생산비 마감(124) — 브랜드·마감 월(기본 지난달)·마감 건 고르고 「확인」으로 본문 확정, 제목은 「확인」마다 덮어씀.
 * 브랜드는 목록을 거르지 않고 제목에만 씀(레거시). 브랜드·월을 바꾸면 목록을 다시 읽고 본문을 비움.
 * 편집은 조건을 바꿀 수 없고 저장 본문만(레거시)
 */
const ProductionCostClosingField = ({ isEditMode, documentId }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const confirmed = useWatch({ control, name: 'productionCostClosing' });
  const { options: brandOptions, error: brandError } = useMyBrandOptions('A');

  const [brandCode, setBrandCode] = useState('');
  const [month, setMonth] = useState<Dayjs | null>(() =>
    dayjs().subtract(1, 'month').startOf('month'),
  );
  const yyyymm = month?.isValid() ? month.format('YYYYMM') : '';

  const {
    data: source,
    isLoading,
    mutate: reloadCandidates,
  } = useSWR<ProductionCostClosingDraftSource>(
    isEditMode || !yyyymm
      ? null
      : getProductionCostClosingDraftSourceUrl({ yyyymm }),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );
  const candidates = useMemo(() => source?.display.candidates ?? [], [source]);

  const [selectedId, setSelectedId] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  // 확인 중 조건을 바꾸고 다시 확인하면 늦게 온 이전 응답이 본문을 덮지 않게
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (brandCode || !brandOptions.length) return;
    setBrandCode(brandOptions[0].value);
  }, [brandOptions, brandCode]);

  const brandErrorShownRef = useRef(false);
  useEffect(() => {
    if (!brandError || brandErrorShownRef.current) return;
    brandErrorShownRef.current = true;
    snackbar.showError({ message: t('브랜드 리스트를 불러올 수 없습니다.') });
  }, [brandError, snackbar, t]);

  // 레거시 콤보: 목록을 다시 받으면 첫 건 선택
  useEffect(() => {
    setSelectedId(candidates[0]?.aprvId ?? '');
  }, [candidates]);

  /** 레거시 re_tax_tax_case — 목록 다시 읽고 본문 비움(제목은 그대로) */
  const resetBody = () => {
    requestIdRef.current += 1;
    setIsConfirming(false);
    setSelectedId(candidates[0]?.aprvId ?? '');
    setValue('productionCostClosing', null, { shouldDirty: true });
  };

  const handleConfirm = async () => {
    const option = candidates.find((c) => c.aprvId === selectedId);
    if (isConfirming || !option || !month || !yyyymm) return;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsConfirming(true);
    const brandName =
      brandOptions.find((o) => o.value === brandCode)?.label ?? '';
    try {
      const data = await getNestedDataFetcher<ProductionCostClosingDraftSource>(
        getProductionCostClosingDraftSourceUrl({
          yyyymm,
          approvalId: option.aprvId,
        }),
      );
      if (requestId !== requestIdRef.current) return;
      const card = data.display.content?.cards?.[0];
      if (!card) throw new Error('card missing');
      setValue(
        'subject',
        buildProductionCostClosingSubject(
          {
            brandName,
            month: month.format('YYYY-MM'),
            caseTitle: productionCostClosingCaseTitle(option),
          },
          t,
        ),
        { shouldDirty: true },
      );
      setValue(
        'productionCostClosing',
        {
          systemId: option.sysId,
          approvalTypeCode: option.aprvTypcd,
          approvalId: option.aprvId,
          content: buildProductionCostClosingContentHtml(card, t),
          yyyymm,
        },
        { shouldDirty: true },
      );
    } catch {
      if (requestId !== requestIdRef.current) return;
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === requestIdRef.current) setIsConfirming(false);
    }
  };

  const detailSource = useMemo<ProductionCostClosingDetailSource | null>(() => {
    if (confirmed?.yyyymm) return { kind: 'draft', yyyymm: confirmed.yyyymm };
    return documentId ? { kind: 'document', documentId } : null;
  }, [confirmed?.yyyymm, documentId]);

  const body = confirmed?.content ? (
    <ProductionCostClosingSnapshot
      content={confirmed.content}
      detailSource={detailSource}
      approvalId={confirmed.approvalId}
    />
  ) : null;

  if (isEditMode) return <FieldBlock>{body}</FieldBlock>;

  return (
    <FieldBlock>
      <ControlRow>
        <Select
          value={brandCode}
          options={brandOptions}
          placeholder={t('브랜드')}
          onChange={(e) => {
            setBrandCode(String(e.target.value));
            resetBody();
            reloadCandidates();
          }}
          sx={{
            ...approvalOutlineSelectSx,
            width: 180,
            height: CONTROL_HEIGHT,
          }}
        />
        <DatePicker
          value={month}
          onChange={(value) => {
            setMonth(value);
            resetBody();
          }}
          views={['year', 'month']}
          openTo="month"
          format="YYYY-MM"
          placeholder="YYYY-MM"
          sx={{ width: 160 }}
        />
        <Select
          value={selectedId}
          options={candidates.map((c) => ({
            value: c.aprvId,
            label: productionCostClosingCaseTitle(c) || c.aprvId,
          }))}
          placeholder={
            isLoading
              ? t('불러오는 중...')
              : candidates.length
              ? t('결재할 건을 선택해주세요')
              : t('진행할 내역이 없습니다.')
          }
          onChange={(e) => setSelectedId(String(e.target.value))}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 240,
            height: CONTROL_HEIGHT,
          }}
        />
        <BoxButton
          type="button"
          variant="keyColor"
          size="medium"
          disabled={isLoading || isConfirming || !selectedId}
          startIcon={
            isConfirming ? <Spinner theme="white" size="16px" /> : undefined
          }
          onClick={handleConfirm}
        >
          {t('확인')}
        </BoxButton>
      </ControlRow>
      {body}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

export default ProductionCostClosingField;
